import React, { useEffect, useState } from "react";
import qs from "qs";
import moment from "moment";
import Table from "../../common/table";
import SearchBox from "../../common/searchBox";
import { useGetRewardAccounts } from "../../../queryHooks/useRewards";
import { formatDollars } from "./rewardConfig";

const formatAmount = (value) =>
	`$${Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const userName = (account) => [account.user?.firstName, account.user?.lastName].filter(Boolean).join(" ") || "-";

const Balance = () => {
	const [searchTerm, setSearchTerm] = useState("");
	const [pageNow, setPageNow] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [tableData, setTableData] = useState({ records: [], total_items: 0, page: 1, items_per_page: 10 });
	const [summary, setSummary] = useState({ redeemedAmount: 0, redeemedPoints: 0, redeemedOrders: 0 });

	const payload = {
		page: pageNow,
		limit: itemsPerPage,
		...(searchTerm ? { search: searchTerm } : {}),
	};
	const { data, isLoading } = useGetRewardAccounts(qs.stringify(payload));
	useEffect(() => {
		if (data?.status === "success") {
			setTableData(data.data);
			if (data.data?.summary) setSummary(data.data.summary);
		}
	}, [data]);

	const columns = [
		{
			label: "User",
			type: "string",
			path: "userId",
			content: (item) => (
				<span title={item.userId}>
					{userName(item)}
					<div className="text-muted small">{item.userId}</div>
				</span>
			),
		},
		{
			label: "Email",
			type: "string",
			path: "email",
			content: (item) => item.user?.email || "-",
		},
		{
			label: "Balance",
			type: "number",
			path: "balance",
			content: (item) => (
				<span>
					<strong>{item.balance.toLocaleString()}</strong>
					<div className="text-muted small">≈ {formatDollars(item.balance)}</div>
				</span>
			),
		},
		{
			label: "Earned",
			type: "number",
			path: "lifetimeEarned",
			content: (item) => item.lifetimeEarned.toLocaleString(),
		},
		{
			label: "Redeemed",
			type: "number",
			path: "lifetimeRedeemed",
			content: (item) => item.lifetimeRedeemed.toLocaleString(),
		},
		{
			label: "Reversed",
			type: "number",
			path: "lifetimeReversed",
			content: (item) => item.lifetimeReversed.toLocaleString(),
		},
		{
			label: "Last Activity",
			type: "string",
			path: "updatedAt",
			content: (item) => (item.updatedAt ? moment(item.updatedAt).format("YYYY-MM-DD HH:mm") : "-"),
		},
	];

	return (
		<div className="module-section">
			<div className="section-header">
				<h3>User Rewards Balance</h3>
				<div className="header-stats">
					<div className="stat-card">
						<div className="stat-label">Users With Points</div>
						<div className="stat-value">{(tableData.total_items || 0).toLocaleString()}</div>
					</div>
					<div className="stat-card" title="Discount given at checkout from redeemed points (OPA funded). Refunded orders are excluded.">
						<div className="stat-label">Redeemed Value</div>
						<div className="stat-value">{formatAmount(summary.redeemedAmount)}</div>
						<div className="stat-sub">
							{summary.redeemedPoints.toLocaleString()} pts · {summary.redeemedOrders.toLocaleString()}{" "}
							{summary.redeemedOrders === 1 ? "order" : "orders"}
						</div>
					</div>
				</div>
			</div>

			<div className="section-filters">
				<SearchBox
					search={(value) => {
						setPageNow(1);
						setSearchTerm(value);
					}}
					searchValue={searchTerm}
				/>
			</div>

			<Table
				columns={columns}
				columnKey="rewards_balance"
				tableData={tableData}
				loading={isLoading}
				setCurrentPage={(page) => setPageNow(page)}
				setCurrentLimit={(limit) => {
					setPageNow(1);
					setItemsPerPage(limit);
				}}
			/>
		</div>
	);
};

export default Balance;
